/**
 * 税計算ドメインの型定義。
 *
 * バックエンド（Java）の `com.smarttax.assistant.tax` / `model` を移植したもので、
 * React に依存しない純粋な型のみを定義する。計算データに `any` を用いない。
 */

/** 給与所得控除の年分別プロファイル。 */
export type SalaryProfile = 'REIWA_6' | 'REIWA_7' | 'REIWA_8';

/** 住宅の取得区分（新築等・買取再販 / 既存（中古））。 */
export type HousingAcquisitionType = 'NEW' | 'USED';

/**
 * 住宅の省エネ性能等による区分（#63）。
 *
 * - `LONG_TERM`: 認定長期優良住宅・認定低炭素住宅
 * - `ZEH`: ZEH 水準省エネ住宅
 * - `ENERGY_SAVING`: 省エネ基準適合住宅
 * - `OTHER`: その他の住宅（省エネ基準を満たさない住宅）
 */
export type HousingLoanCategory = 'LONG_TERM' | 'ZEH' | 'ENERGY_SAVING' | 'OTHER';

/** ふるさと納税の控除の受け方（確定申告 / ワンストップ特例）。 */
export type FurusatoFilingMethod = 'FINAL_RETURN' | 'ONE_STOP';

/** 家族構成と本人属性の入力モデル（バックエンド FamilyData の移植 + #46/#63 拡張）。 */
export interface FamilyData {
  /** 配偶者の有無。 */
  spouse: boolean;
  /** 配偶者の合計所得（万円単位）。 */
  spouseIncome: number;
  /** 配偶者の年齢（歳）。0 は未入力。70歳以上は老人控除対象配偶者として加算する。 */
  spouseAge: number;
  /** 一般の控除対象扶養親族（16歳以上19歳未満、23歳以上70歳未満）の人数。 */
  dependents: number;
  /** 一般扶養親族のうち16〜18歳の人数（`dependents` の内数）。子育て世帯特例の判定に用いる。 */
  dependents16To18: number;
  /** 一般扶養親族の合計所得金額（万円）。i 番目が i 人目の所得。未指定（空配列）は全員 0（所得要件を満たす）。 */
  dependentIncomes: number[];
  /** 16歳未満（所得税・住民税とも扶養控除の対象外。件数のみ保持）。 */
  dependentsUnder16: number;
  /** 特定扶養親族（19歳以上23歳未満）の人数。所得要件を超える者は特定親族特別控除の対象。 */
  specificDependents: number;
  /** 特定扶養親族の合計所得金額（万円）。i 番目が i 人目の所得。未指定（空配列）は全員 0。 */
  specificDependentIncomes: number[];
  /** 老人扶養親族（70歳以上）。 */
  elderlyDependents: number;
  /** 同居老親等（elderlyDependents の内数）。 */
  elderlyDependentsLivingTogether: number;
  /** 本人が勤労学生。 */
  workingStudent: boolean;
  /** 一般生命保険料の年間払込額（円）。 */
  lifeInsuranceGeneral: number;
  /** 介護医療保険料の年間払込額（円）。 */
  lifeInsuranceMedical: number;
  /** 個人年金保険料の年間払込額（円）。 */
  lifeInsurancePension: number;
  /** 住宅への居住開始年（西暦）。0 は住宅ローン控除を適用しない。 */
  housingMoveInYear: number;
  /** 住宅への居住開始月（1〜12）。0 は未入力（1月として扱う）。 */
  housingMoveInMonth: number;
  /** 住宅の取得区分（新築等・買取再販 / 既存）。 */
  housingAcquisition: HousingAcquisitionType;
  /** 省エネ性能等による住宅区分。 */
  housingCategory: HousingLoanCategory;
  /** 子育て世帯・若者夫婦世帯（借入限度額の上乗せ判定）。 */
  housingChildRearing: boolean;
  /**
   * 特定取得・特別特定取得（消費税率 8%・10% 等）に該当するか。
   * 住民税分の控除限度額が 5% ではなく 7%（最大 136,500 円）となる場合に true。
   */
  housingSpecialAcquisition: boolean;
  /** 住宅ローン等の年末残高（円）。 */
  housingYearEndBalance: number;
  /**
   * 源泉徴収票の「住宅借入金等特別控除可能額」（円）。0 は年末残高から計算する。
   * 1 円でも指定された場合はこの額を控除可能額として優先する。
   */
  housingCreditPossibleAmount: number;
  /**
   * 本人の合計所得金額（万円）。適用要件（2,000 万円以下）の判定に用いる。
   * 0 は給与年収から自動計算した額を用いる。
   */
  housingTotalIncomeMan: number;
  /** ふるさと納税の控除の受け方（確定申告 / ワンストップ特例）。 */
  furusatoFilingMethod: FurusatoFilingMethod;
}

/** 家族情報の既定値。 */
export function createFamilyData(overrides: Partial<FamilyData> = {}): FamilyData {
  return {
    spouse: false,
    spouseIncome: 0,
    spouseAge: 0,
    dependents: 0,
    dependents16To18: 0,
    dependentIncomes: [],
    dependentsUnder16: 0,
    specificDependents: 0,
    specificDependentIncomes: [],
    elderlyDependents: 0,
    elderlyDependentsLivingTogether: 0,
    workingStudent: false,
    lifeInsuranceGeneral: 0,
    lifeInsuranceMedical: 0,
    lifeInsurancePension: 0,
    housingMoveInYear: 0,
    housingMoveInMonth: 0,
    housingAcquisition: 'NEW',
    housingCategory: 'ENERGY_SAVING',
    housingChildRearing: false,
    housingSpecialAcquisition: false,
    housingYearEndBalance: 0,
    housingCreditPossibleAmount: 0,
    housingTotalIncomeMan: 0,
    furusatoFilingMethod: 'FINAL_RETURN',
    ...overrides,
  };
}

/** 扶養親族1人分の UI 入力行。 */
export interface DependentRow {
  /** 年齢（歳）。0〜120。16歳未満は扶養控除の対象外。 */
  age: number;
  /** 合計所得金額（万円）。扶養控除・特定親族特別控除の所得要件に用いる。 */
  incomeMan: number;
  /** 同居老親等（70歳以上の行のみ有効）。 */
  livingTogether: boolean;
}

/**
 * UI 入力モデル。ドメインの `FamilyData` と異なり、配偶者は「給与年収（万円）」で入力し、
 * 扶養親族は年齢・合計所得の行配列で入力する。
 *
 * ドメインの `FamilyData.spouseIncome` は「配偶者の合計所得（万円）」であるため、
 * UI 層で選択年分の給与所得控除を適用して合計所得へ換算してからエンジンへ渡す。
 * 扶養親族の区分別人数・所得配列は行から導出する（`toFamilyData` を参照）。
 */
export interface FamilyFormData {
  spouse: boolean;
  /** 配偶者の給与年収（万円。給与収入のみを想定）。 */
  spouseAnnualIncome: number;
  /** 配偶者の年齢（歳）。0 は未入力。 */
  spouseAge: number;
  /** 扶養親族の入力行（1人=1行）。 */
  dependents: DependentRow[];
  workingStudent: boolean;
  /** 一般生命保険料の年間払込額（円）。 */
  lifeInsuranceGeneral: number;
  /** 介護医療保険料の年間払込額（円）。 */
  lifeInsuranceMedical: number;
  /** 個人年金保険料の年間払込額（円）。 */
  lifeInsurancePension: number;
  /** 住宅への居住開始年（西暦）。0 は住宅ローン控除を適用しない。 */
  housingMoveInYear: number;
  /** 住宅への居住開始月（1〜12）。0 は未入力（1月として扱う）。 */
  housingMoveInMonth: number;
  housingAcquisition: HousingAcquisitionType;
  housingCategory: HousingLoanCategory;
  housingChildRearing: boolean;
  housingSpecialAcquisition: boolean;
  /** 住宅ローン等の年末残高（円）。 */
  housingYearEndBalance: number;
  /** 源泉徴収票の「住宅借入金等特別控除可能額」（円）。0 は年末残高から計算する。 */
  housingCreditPossibleAmount: number;
  /** ふるさと納税の控除の受け方（確定申告 / ワンストップ特例）。 */
  furusatoFilingMethod: FurusatoFilingMethod;
}

/** UI 入力モデルの既定値。 */
export function createFamilyFormData(overrides: Partial<FamilyFormData> = {}): FamilyFormData {
  return {
    spouse: false,
    spouseAnnualIncome: 0,
    spouseAge: 0,
    dependents: [],
    workingStudent: false,
    lifeInsuranceGeneral: 0,
    lifeInsuranceMedical: 0,
    lifeInsurancePension: 0,
    housingMoveInYear: 0,
    housingMoveInMonth: 0,
    housingAcquisition: 'NEW',
    housingCategory: 'ENERGY_SAVING',
    housingChildRearing: false,
    housingSpecialAcquisition: false,
    housingYearEndBalance: 0,
    housingCreditPossibleAmount: 0,
    furusatoFilingMethod: 'FINAL_RETURN',
    ...overrides,
  };
}

/**
 * 年分ごとの税制ルール（バックエンド TaxRules の移植）。
 * すべて純粋関数で、副作用を持たない。
 */
export interface TaxRules {
  /** 所得年（年分）。例: 2026。 */
  incomeYear(): number;
  /** この所得に課される住民税の年度（所得年 + 1）。 */
  residentFiscalYear(): number;

  /** 給与所得控除額。乗算は円未満切捨て。 */
  salaryDeduction(gross: number): number;
  /** 合計所得金額に応じた所得税の基礎控除。 */
  basicDeduction(totalIncome: number): number;
  /** 合計所得金額に応じた住民税の基礎控除（43万 / 29万 / 15万 / 0）。 */
  residentBasicDeduction(totalIncome: number): number;

  /** 配偶者控除の所得要件（配偶者の合計所得金額の上限）。 */
  spouseIncomeThreshold(): number;
  /** 配偶者特別控除の所得上限（133 万円）。 */
  spouseSpecialDeductionLimit(): number;
  /** 所得税の配偶者控除・配偶者特別控除額。elderly が true なら老人控除対象配偶者（70歳以上）。 */
  spouseDeduction(taxpayerIncome: number, spouseIncome: number, elderly?: boolean): number;
  /** 住民税の配偶者控除・配偶者特別控除額。elderly が true なら老人控除対象配偶者。 */
  residentSpouseDeduction(taxpayerIncome: number, spouseIncome: number, elderly?: boolean): number;

  /** 扶養控除の所得要件（所得税）。 */
  dependentIncomeThresholdIncome(): number;
  /** 扶養控除の所得要件（住民税）。 */
  dependentIncomeThresholdResident(): number;

  /** 所得税の扶養控除合計額。 */
  dependentDeductionIncome(
    general: number,
    specific: number,
    elderly: number,
    elderlyLivingTogether: number,
  ): number;
  /** 住民税の扶養控除合計額。 */
  dependentDeductionResident(
    general: number,
    specific: number,
    elderly: number,
    elderlyLivingTogether: number,
  ): number;

  /**
   * 特定親族特別控除（所得税）。特定親族の合計所得金額（円）に対する控除額。
   * 令和8年分以後のみ適用され、それ以前の年分は常に 0。
   */
  specificRelativeDeductionIncome(specificIncome: number): number;

  /**
   * 特定親族特別控除（住民税）。令和8年度課税（所得年2025）以後のみ適用され、
   * 下限は年分の住民税扶養所得要件（令和8年度は58万円、令和9年度は62万円）に一致する。
   */
  specificRelativeDeductionResident(specificIncome: number): number;

  /**
   * 配偶者控除・配偶者特別控除の人的控除額の差（調整控除の基礎）。
   * 配偶者特別控除の差は令和8年度課税（所得年2025）以後は 0。
   */
  spouseDeductionDifference(taxpayerIncome: number, spouseIncome: number, elderly?: boolean): number;

  /**
   * 生命保険料控除（所得税）。一般・介護医療・個人年金の合計（上限 12 万円）。
   * childRearing が true かつ令和8・9年分のとき、一般生命保険料控除の上限を 6 万円に引き上げる。
   */
  lifeInsuranceDeductionIncome(
    general: number,
    medical: number,
    pension: number,
    childRearing: boolean,
  ): number;
  /** 生命保険料控除（住民税）。合計上限 7 万円。 */
  lifeInsuranceDeductionResident(general: number, medical: number, pension: number): number;

  /** 所得税と住民税の人的控除額の差（基礎控除分・一律 5 万円）。 */
  basicDeductionDifference(): number;

  /** 住民税の調整控除。taxableIncome は住民税の課税所得、humanDeductionDifference は人的控除差の合計、totalIncome は合計所得金額。 */
  adjustmentCredit(taxableIncome: number, humanDeductionDifference: number, totalIncome: number): number;

  /** 住民税（所得割）の非課税限度額。扶養親族等がいない場合は 45 万円。 */
  residentIncomeLevyNonTaxableLimit(householdCount: number, hasDependents: boolean): number;
  /** 住民税（均等割）の非課税限度額。扶養親族等がいない場合は 45 万円。 */
  residentEqualLevyNonTaxableLimit(householdCount: number, hasDependents: boolean): number;

  /** 所得税の勤労学生控除（27万）。 */
  workingStudentDeductionIncome(): number;
  /** 住民税の勤労学生控除（26万）。 */
  workingStudentDeductionResident(): number;

  /** 課税所得に応じた所得税率（速算表）。 */
  incomeTaxRate(taxable: number): number;
  /** 課税所得に応じた速算表の控除額。 */
  incomeTaxDeduction(taxable: number): number;
  /** 所得税に付加税（復興・防衛）を加算した額（円未満切捨て）。 */
  incomeTaxWithSurtax(incomeTax: number): number;

  /** 付加税率の合計。 */
  surtaxRate(): number;
  reconstructionSurtaxRate(): number;
  defenseSurtaxRate(): number;

  /** 住民税の所得割率。 */
  residentTaxRate(): number;
  /** 住民税の均等割（森林環境税を含む 6,000 円）。 */
  residentEqualLevy(): number;

  // --- 住宅ローン控除（住宅借入金等特別控除。#63） ---

  /** 住宅ローン控除の控除率（年末残高等 × 控除率）。令和4年以降入居は 0.7%。 */
  housingLoanRate(): number;
  /** 住宅ローン控除の適用要件となる合計所得金額の上限（2,000 万円）。 */
  housingLoanIncomeRequirement(): number;
  /** 年分ごとの住宅ローン控除が想定する居住年の下限・上限（西暦）。 */
  housingLoanSupportedMoveInYears(): { min: number; max: number };
  /**
   * 居住年・取得区分・住宅区分・子育て世帯等に応じた控除対象借入限度額（円）。
   * 対象外（省エネ基準を満たさない新築等）は 0。
   */
  housingLoanBorrowingLimit(
    moveInYear: number,
    acquisition: HousingAcquisitionType,
    category: HousingLoanCategory,
    childRearing: boolean,
  ): number;
  /**
   * 住民税の住宅借入金等特別税額控除の限度額率。
   * 平成28年〜令和3年入居（特定取得等）は 7%、令和4年以降入居は原則 5%
   * （令和4年入居の特定取得等は 7%）。
   */
  housingLoanResidentCreditLimitRate(
    moveInYear: number,
    moveInMonth: number,
    specialAcquisition: boolean,
  ): number;
  /** 住民税の住宅借入金等特別税額控除の限度額（円）。限度額率に応じて 97,500 / 136,500。 */
  housingLoanResidentCreditLimitCap(
    moveInYear: number,
    moveInMonth: number,
    specialAcquisition: boolean,
  ): number;
  /**
   * 住民税の住宅ローン控除限度額の計算基礎に加算する額（円）。
   * 所得税の基礎控除額 − 48 万円（0 円未満は 0）。令和8年度課税以降の調整。
   */
  housingLoanResidentCreditBaseAdjustment(incomeTaxBasicDeduction: number): number;
}

/** 計算明細の 1 ステップ。 */
export interface CalculationStep {
  label: string;
  value: number;
  formula?: string;
}

/** 計算明細（バックエンド CalculationDetail の移植）。 */
export interface CalculationDetail {
  title: string;
  steps: CalculationStep[];
}

/** 計算結果（バックエンド TaxCalculationWithDetails と同形）。 */
export interface TaxCalculationWithDetails {
  annualIncome: number;
  socialInsurance: number;
  incomeTax: number;
  incomeTaxWithSurtax: number;
  residentTax: number;
  furusatoLimit: number;
  netAnnualIncome: number;
  definedContribution: number;
  healthInsurance: number;
  employmentInsurance: number;
  nationalPension: number;
  taxYear: number;
  residentFiscalYear: number;
  /** 住宅ローン控除が適用されたか（居住年等が入力され要件を満たす場合に true）。 */
  housingLoanApplicable: boolean;
  /** 住宅ローン控除を適用しない理由（該当なしは空文字）。 */
  housingLoanNote: string;
  /** 住宅ローン控除の可能額（円）。 */
  housingLoanCreditAvailable: number;
  /** 住宅ローン控除のうち所得税から控除した額（円）。 */
  housingLoanCreditIncome: number;
  /** 住宅ローン控除のうち住民税（所得割）から控除した額（円）。 */
  housingLoanCreditResident: number;
  /** ふるさと納税の制度上の控除上限額（住宅ローン控除を考慮しない。円）。 */
  furusatoInstitutionalLimit: number;
  /** ふるさと納税の「損しない上限」（申告方法と住宅ローン控除を考慮。円）。 */
  furusatoSafeLimit: number;
  /** 制度上の上限 − 損しない上限（損する可能性のある差額。円）。 */
  furusatoSafeDifference: number;
  /** 損しない上限の算出に用いた申告方法。 */
  furusatoFilingMethod: FurusatoFilingMethod;
  calculationDetails: Record<string, CalculationDetail>;
}

/** 税計算の入力。 */
export interface TaxCalculationInput {
  annualIncome: number;
  healthInsurance?: number;
  employmentInsurance?: number;
  nationalPension?: number;
  definedContribution?: number;
  familyData?: FamilyData;
  /** 計算対象の所得年（年分）。未指定は既定年（2026）。 */
  taxYear?: number;
}
